// HELP.A2b (#2055, ADR-003 §10.3) — the capture-only surface.
//
// MUST NOT IMPLY AN ANSWER IS FORTHCOMING. This is the criterion the story
// prompt itself flags as most likely to soften during implementation. There
// is deliberately no chat-transcript layout, no "typing…" affordance, no
// message bubbles, and no attachment control (ADR-003 §7 — attachments are
// intake-only, and this is not the intake path; see #2136 for the separate,
// unowned gap on the /chat route that this surface must not reintroduce by
// pattern-copying a chat-shaped component).
//
// No A5 Help panel exists yet (recon finding #5) — this renders as a
// standalone card wherever it is mounted, not slotted into shared chrome
// that does not exist. See Sidebar.tsx for the one entry point this story
// adds.

import { useId, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';

interface Props {
  projectId: string;
}

type Status = 'idle' | 'submitting' | 'captured' | 'error';

const QUESTION_MAX = 4000;

export default function HelpCaptureForm({ projectId }: Props) {
  const [questionText, setQuestionText] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const textareaId = useId();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = questionText.trim();
    if (!trimmed || status === 'submitting') return;

    setStatus('submitting');
    try {
      await apiFetch('/help/capture', {
        method: 'POST',
        body: JSON.stringify({ projectId, questionText: trimmed }),
      });
      setStatus('captured');
      setQuestionText('');
    } catch (err) {
      // Deliberately generic — the same stealth posture the backend gate
      // maintains (routes/help-capture.ts). A flag-off 404 and a genuine
      // network failure must not be distinguishable in the UI either, or
      // the client-side copy becomes the leak the server-side generic-404
      // was built to prevent.
      void err;
      setStatus('error');
    }
  }

  if (status === 'captured') {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="help-capture-confirmation"
        style={confirmationStyle}
      >
        <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>Question received.</p>
        <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 13 }}>
          We don&apos;t answer questions here yet — this helps us build that. No reply is coming
          to this screen.
        </p>
        <button
          type="button"
          data-testid="help-capture-ask-another"
          onClick={() => setStatus('idle')}
          style={secondaryButtonStyle}
        >
          Ask another question
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      <label htmlFor={textareaId} style={labelStyle}>
        Ask a quick question
      </label>
      <p style={{ margin: '2px 0 8px', color: 'var(--text-secondary)', fontSize: 12 }}>
        We record this to build better in-product help. It is not answered here, and no
        attachments are accepted on this form.
      </p>
      <textarea
        id={textareaId}
        data-testid="help-capture-textarea"
        value={questionText}
        maxLength={QUESTION_MAX}
        onChange={e => setQuestionText(e.target.value)}
        placeholder="What are you trying to do?"
        rows={3}
        disabled={status === 'submitting'}
        style={textareaStyle}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {questionText.length}/{QUESTION_MAX}
        </span>
        <button
          type="submit"
          data-testid="help-capture-submit"
          disabled={!questionText.trim() || status === 'submitting'}
          style={primaryButtonStyle(!questionText.trim() || status === 'submitting')}
        >
          {status === 'submitting' ? 'Sending…' : 'Send'}
        </button>
      </div>
      {status === 'error' && (
        <p role="alert" data-testid="help-capture-error" style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          Something went wrong. Please try again.
        </p>
      )}
    </form>
  );
}

// Re-exported so a caller can distinguish a real network/server error from
// other failure shapes if it ever needs to (currently unused deliberately —
// see the generic-error comment above).
export { ApiError };

const formStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: 16,
  fontFamily: 'var(--font-body)',
  maxWidth: 420,
};

const confirmationStyle: React.CSSProperties = {
  ...formStyle,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 600,
  fontSize: 14,
  color: 'var(--text)',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 10px',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--text)',
  outline: 'none',
  resize: 'vertical',
};

function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--primary-light)' : 'var(--primary)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    padding: '8px 16px',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const secondaryButtonStyle: React.CSSProperties = {
  marginTop: 12,
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 12px',
  fontFamily: 'inherit',
  fontSize: 12,
  color: 'var(--text)',
  cursor: 'pointer',
};
